/**
 * <hugh-reviews>: filters the review list of sections/hugh-reviews.liquid by stars or photos and
 * sorts it (newest, oldest, highest, lowest; the server renders newest first).
 * The chips and the histogram rows set one filter; "All" clears it. While filtered, the "Show more"
 * fold opens so every match shows. A review photo opens large in the section's dialog. Without JS
 * the full list and the fold stay as rendered, and a photo link opens the image itself.
 */
class HughReviews extends HTMLElement {
  connectedCallback() {
    const filters = this.querySelector('[data-filters]');
    if (filters instanceof HTMLElement) filters.hidden = false;
    // A star with no review in the list has no chip, and its histogram row cannot be pressed.
    for (const control of this.querySelectorAll('[data-filter]')) {
      if (!(control instanceof HTMLElement) || !/^[1-5]$/.test(control.dataset.filter || '')) continue;
      const none = !this.querySelector(`.hugh-review[data-score="${control.dataset.filter}"]`);
      if (control.classList.contains('hugh-reviews__chip')) control.hidden = none;
      else if (control instanceof HTMLButtonElement) control.disabled = none;
    }
    this.querySelector('[data-sort]')?.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement) this.#sort(event.target.value);
    });
    this.addEventListener('click', (event) => {
      // A click on the dialog's backdrop (the dialog element itself) closes it.
      if (event.target instanceof HTMLDialogElement) {
        event.target.close();
        return;
      }
      const photo = event.target instanceof Element ? event.target.closest('[data-review-photo]') : null;
      if (photo instanceof HTMLAnchorElement && this.#show(photo)) {
        event.preventDefault();
        return;
      }
      const button = event.target instanceof Element ? event.target.closest('[data-filter]') : null;
      if (!(button instanceof HTMLElement) || !this.contains(button)) return;
      const value = button.dataset.filter || 'all';
      const same = button.getAttribute('aria-pressed') === 'true' && value !== 'all';
      this.#apply(same ? 'all' : value);
    });
  }

  /**
   * Opens a review photo in the dialog. Only Shopify CDN images are shown.
   * @param {HTMLAnchorElement} link
   * @returns {boolean} whether the dialog opened
   */
  #show(link) {
    const dialog = this.querySelector('[data-viewer]');
    const img = this.querySelector('[data-viewer-img]');
    const url = new URL(link.href, window.location.href);
    if (!(dialog instanceof HTMLDialogElement) || !(img instanceof HTMLImageElement) || url.hostname !== 'cdn.shopify.com') return false;
    img.width = Number(link.dataset.w) || 1600;
    img.height = Number(link.dataset.h) || 1600;
    img.src = url.href;
    img.alt = link.querySelector('img')?.alt || '';
    dialog.showModal();
    return true;
  }

  /**
   * Reorders the reviews; the first `data-shown` stay in the list, the rest go into the
   * "Show more" fold, as the server renders them.
   * @param {string} order 'newest', 'oldest', 'highest' or 'lowest'
   */
  #sort(order) {
    const reviews = [...this.querySelectorAll('.hugh-review')].filter((r) => r instanceof HTMLElement);
    const date = (/** @type {HTMLElement} */ r) => r.dataset.date || '';
    const score = (/** @type {HTMLElement} */ r) => Number(r.dataset.score) || 0;
    reviews.sort((a, b) => {
      if (order === 'oldest') return date(a).localeCompare(date(b));
      if (order === 'highest') return score(b) - score(a) || date(b).localeCompare(date(a));
      if (order === 'lowest') return score(a) - score(b) || date(b).localeCompare(date(a));
      return date(b).localeCompare(date(a));
    });
    const fold = this.querySelector('.hugh-reviews__more');
    const list = this.querySelector('.hugh-reviews__list');
    if (!list) return;
    const shown = Number(this.dataset.shown) || reviews.length;
    reviews.forEach((review, i) => {
      if (fold && i >= shown) fold.append(review);
      else if (fold) list.insertBefore(review, fold);
      else list.append(review);
    });
  }

  /** @param {string} value 'all', '1'–'5' or 'photos' */
  #apply(value) {
    const all = value === 'all';
    this.toggleAttribute('data-filter', !all);
    let shown = 0;
    for (const review of this.querySelectorAll('.hugh-review')) {
      if (!(review instanceof HTMLElement)) continue;
      const match = all || (value === 'photos' ? review.hasAttribute('data-photos') : review.dataset.score === value);
      review.toggleAttribute('data-hidden', !match);
      if (match) shown += 1;
    }
    const fold = this.querySelector('.hugh-reviews__more');
    if (fold instanceof HTMLDetailsElement && !all) fold.open = true;
    for (const button of this.querySelectorAll('[data-filter]')) {
      if (button instanceof HTMLElement) button.setAttribute('aria-pressed', String(button.dataset.filter === value));
    }
    const empty = this.querySelector('[data-empty]');
    if (empty instanceof HTMLElement) empty.hidden = shown > 0;
  }
}

if (!customElements.get('hugh-reviews')) customElements.define('hugh-reviews', HughReviews);
